/**
 * useAdminRibbon — Declarative ribbon config for the Admin Console.
 * 
 * Converts the former sidebar navigation into ribbon groups.
 * Tab-wrapper pages (Pessoas, Licenças, Segurança, Integrações, Plataforma)
 * become dropdown menus.
 */

import React, { useMemo } from 'react';
import {
  BadgeCheck,
  Building2,
  FileText,
  Globe,
  LayoutDashboard,
  Lock,
  ScrollText,
  Server,
  Shield,
  Users,
  KeyRound,
  ShieldCheck,
  ClipboardList,
  Plug,
  Store,
  Zap,
  Radio,
  Terminal,
  Clock,
} from 'lucide-react';
import type { RibbonConfig, RibbonAction } from '../types/ribbon';

export interface AdminRibbonDeps {
  onNavigate: (path: string) => void;
  currentPath: string;
  slots?: { afterGroups?: React.ReactNode };
  /** Extra action buttons rendered as a ribbon group after nav buttons */
  contextActions?: RibbonAction[];
}

function isActive(current: string, target: string, extra?: string[]): boolean {
  if (target === '/administracao') {
    return current === '/administracao' || current === '/administracao/dashboard';
  }
  if (current.startsWith(target)) return true;
  return extra?.some((p) => current.startsWith(p)) ?? false;
}

export function useAdminRibbon(deps: AdminRibbonDeps): RibbonConfig {
  const { onNavigate, currentPath, slots, contextActions } = deps;

  return useMemo<RibbonConfig>(() => ({
    moduleId: 'admin',
    groups: [
      // ─── Organização (LEFT) — dropdown ──────────
      {
        id: 'organizacao',
        label: 'Organização',
        position: 'left',
        actions: [
          {
            id: 'org-menu',
            label: 'Organização',
            icon: Building2,
            onClick: () => onNavigate('/administracao/dashboard'),
            active: isActive(currentPath, '/administracao') || isActive(currentPath, '/administracao/organizacao'),
            dropdown: [
              { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, onClick: () => onNavigate('/administracao/dashboard') },
              { id: 'configuracoes', label: 'Configurações', icon: Building2, onClick: () => onNavigate('/administracao/organizacao') },
            ],
          },
        ],
      },
      // ─── Pessoas (LEFT) — dropdown ──────────────
      // ESCOPO SUPER ADMIN: apenas gerência global de Usuários e Governança de
      // Tempos. Cargos / Departamentos / Hierarquia foram movidos para o escopo
      // interno do cliente (Organização → Vínculos e Cargos).
      {
        id: 'pessoas',
        label: 'Pessoas',
        position: 'left',
        actions: [
          {
            id: 'pessoas-menu',
            label: 'Pessoas',
            icon: Users,
            onClick: () => onNavigate('/administracao/pessoas'),
            active: isActive(currentPath, '/administracao/pessoas', ['/administracao/usuarios', '/administracao/governanca-tempos']),
            dropdown: [
              { id: 'usuarios', label: 'Usuários', icon: Users, onClick: () => onNavigate('/administracao/pessoas?tab=usuarios') },
              { id: 'governanca-tempos', label: 'Governança de Tempos', icon: Clock, onClick: () => onNavigate('/administracao/governanca-tempos') },
            ],
          },
        ],
      },
      // ─── Comercial (LEFT) ───────────────────────
      {
        id: 'comercial',
        label: 'Comercial',
        position: 'left',
        actions: [
          {
            id: 'clientes',
            label: 'Clientes',
            icon: Globe,
            onClick: () => onNavigate('/administracao/clientes'),
            active: isActive(currentPath, '/administracao/clientes'),
          },
          {
            id: 'licencas-menu',
            label: 'Licenças',
            icon: BadgeCheck,
            onClick: () => onNavigate('/administracao/licencas'),
            active: isActive(currentPath, '/administracao/licencas', ['/administracao/billing']),
            dropdown: [
              { id: 'licencas', label: 'Licenças', icon: BadgeCheck, onClick: () => onNavigate('/administracao/licencas?tab=licencas') },
              { id: 'billing', label: 'Billing', icon: KeyRound, onClick: () => onNavigate('/administracao/licencas?tab=billing') },
            ],
          },
        ],
      },
      // ─── Segurança (LEFT) — dropdown ────────────
      {
        id: 'seguranca',
        label: 'Segurança',
        position: 'left',
        actions: [
          {
            id: 'seguranca-menu',
            label: 'Segurança',
            icon: Lock,
            onClick: () => onNavigate('/administracao/seguranca'),
            active: isActive(currentPath, '/administracao/seguranca', ['/administracao/permissoes', '/administracao/auditoria']),
            dropdown: [
              { id: 'permissoes', label: 'Permissões', icon: ShieldCheck, onClick: () => onNavigate('/administracao/seguranca?tab=permissoes') },
              { id: 'politicas', label: 'Políticas', icon: ClipboardList, onClick: () => onNavigate('/administracao/seguranca?tab=politicas') },
              { id: 'auditoria', label: 'Auditoria', icon: ScrollText, onClick: () => onNavigate('/administracao/auditoria') },
            ],
          },
        ],
      },
      // ─── Sistema (LEFT) — dropdown ──────────────
      {
        id: 'sistema',
        label: 'Sistema',
        position: 'left',
        actions: [
          {
            id: 'sistema-menu',
            label: 'Sistema',
            icon: Server,
            onClick: () => onNavigate('/administracao/templates'),
            active: isActive(currentPath, '/administracao/templates') || isActive(currentPath, '/administracao/sistema'),
            dropdown: [
              { id: 'templates', label: 'Templates', icon: FileText, onClick: () => onNavigate('/administracao/templates') },
              { id: 'monitoria', label: 'Monitoria', icon: Server, onClick: () => onNavigate('/administracao/sistema') },
            ],
          },
        ],
      },
      // ─── Plataforma (LEFT) — dropdown ───────────
      {
        id: 'plataforma',
        label: 'Plataforma',
        position: 'left',
        actions: [
          {
            id: 'plataforma-menu',
            label: 'Plataforma',
            icon: Shield,
            onClick: () => onNavigate('/administracao/plataforma'),
            active: isActive(currentPath, '/administracao/plataforma', ['/administracao/marketplace', '/administracao/automacoes', '/administracao/event-bus', '/administracao/developers', '/administracao/integracao', '/administracao/api-keys', '/administracao/integracoes']),
            dropdown: [
              { id: 'integracoes', label: 'Integrações', icon: Plug, onClick: () => onNavigate('/administracao/integracao?tab=integracoes') },
              { id: 'api-keys', label: 'API Keys', icon: KeyRound, onClick: () => onNavigate('/administracao/integracao?tab=api-keys') },
              { divider: true, id: 'div-1', label: '', onClick: () => {} },
              { id: 'marketplace', label: 'Marketplace', icon: Store, onClick: () => onNavigate('/administracao/plataforma?tab=marketplace') },
              { id: 'automacoes', label: 'Automações', icon: Zap, onClick: () => onNavigate('/administracao/plataforma?tab=automacoes') },
              { id: 'event-bus', label: 'Event Bus', icon: Radio, onClick: () => onNavigate('/administracao/plataforma?tab=event-bus') },
              { id: 'developers', label: 'Developers', icon: Terminal, onClick: () => onNavigate('/administracao/plataforma?tab=developers') },
            ],
          },
        ],
      },
      // ─── Context Actions (dynamic per page) ─────
      ...(contextActions?.length ? [{
        id: 'context-actions',
        label: 'Ações',
        position: 'right' as const,
        actions: contextActions,
      }] : []),
    ],
    slots: slots ? { afterGroups: slots.afterGroups } : undefined,
  }), [onNavigate, currentPath, slots, contextActions]);
}
